import { toast } from 'react-toastify'
import CustomToast from '../components/custom-toast'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { AlertCircle } from 'react-feather'
import {
  isRefreshTokenExpired,
  isUserLoggedIn,
  getToken,
  getAuthTokenAuthorization,
  isTokenExpired,
  refreshAccessToken,
  clearLocalstorageAuthKeys,
  docRequests
} from 'utility/Utils'
import { isAuthenticatedRoute, routerPaths } from 'router/routes/paths'
import { emitRefreshOrAccessTokenChangedEvent } from 'utility/context/Token'
import NotEmpty from 'utility/NotEmpty'

const MySwal = withReactContent(Swal)

const API_URL = `${process.env.REACT_APP_API_URL}/`

const permiteMultiplosLogins = `${process.env.REACT_APP_ALLOW_MULTIPLE_LOGINS}`

let alertedMultipleLogins = false

let nextCheck = Math.max(Math.random() * 60000, 1000)

const normalizeAccessToken = token => {
  if (typeof token !== 'string') return ''

  const trimmedToken = token.trim()
  if (trimmedToken.startsWith('"') && trimmedToken.endsWith('"')) {
    return trimmedToken.slice(1, -1)
  }

  return trimmedToken
}

const setHeaders = (content_type, check) => {
  const headers = new Headers()
  const accessToken = normalizeAccessToken(getToken())

  if (content_type !== '') headers.append('Content-Type', content_type)

  if (check && accessToken) headers.append('Authorization', `Bearer ${accessToken}`)

  if (permiteMultiplosLogins === 'N') { // valida se pode fazer mais de 1 login por usuário simultaneamente
    if (check) headers.append('AuthTokenAuthorization', `${getAuthTokenAuthorization()}`)
  }

  return headers
}

const handleAlertMoreThanOneLogOn = async () => {
  return MySwal.fire({
    title: '<strong>Atenção!</strong>',
    text: 'Observamos que sua conta foi acessada de mais de um local simultaneamente. Para garantir a segurança de seus dados e manter a integridade do sistema, você será desconectado dessa sessão.',
    iconHtml: <AlertCircle size={80} />,
    icon: 'warning',
    padding: '4px 0 43px 0',
    width: '512px',
    showCancelButton: false,
    confirmButtonText: 'Ok',
    customClass: {
      confirmButton: 'btn btn-primary'
    },
    buttonsStyling: false,
    allowOutsideClick: false,
    allowEscapeKey: false
  }).then(function (result) {
    if (result.value) {
      clearLocalstorageAuthKeys()
      window.location.href = routerPaths.auth.login
      alertedMultipleLogins = false
    }
  })
}

const parseResp = async (resp, responseType) => {
  if (resp) {
    if (resp.status !== 204) {
      let json = null
      try {
        if (responseType === 'blob') {
          json = await resp.blob()
        } else {
          json = await resp.json()
        }
      } catch (error) {
        console.error(error)
      }
      if (resp.status > 300) {
        if (resp.status === 401) {
          emitRefreshOrAccessTokenChangedEvent()
          return
        }
        if (resp.status === 406) {
          if (!alertedMultipleLogins) {
            alertedMultipleLogins = true
            handleAlertMoreThanOneLogOn()
          }
          return
        }
        if (resp.status === 413) {
          return toast.error(
            <CustomToast
              title='Erro!'
              message='A solicitação foi grande demais. Por favor, reenvie em partes menores ou com anexos reduzidos.'
            />
          )
        }
        if (resp.status === 500) {
          return toast.error(
            <CustomToast
              title='Erro!'
              message='Ocorreu um erro inesperado, favor tentar novamente ou contactar o suporte!'
            />
          )
        }
        if (json?.erro?.codigo === "attachment_content_validation") {
          return json
        }
        if (json?.mensagens && Array.isArray(json.mensagens) && NotEmpty(json.mensagens)) {
          if (json.mensagens.some(mensagem => mensagem.includes("E001"))) {
            return json
          }
          if (!toast.isActive('toastDefaultError')) {
            toast.error(
              <CustomToast
                title='Erro!'
                message={`${json.mensagens}`}
              />,
              { autoClose: true, toastId: 'toastDefaultError' }
            )
          } else {
            toast.update(
              <CustomToast
                title='Erro!'
                message={`${json.mensagens}`}
              />,
              { autoClose: true, toastId: 'toastDefaultError' }
            )
          }
          if (json.ValidationErrors) {
            return json
          }
        }
        if (json?.erros && Array.isArray(json.erros) && NotEmpty(json.erros)) {
          throw new Error(json.erros.join("; "))
        }
      } else if (json) return json
    } else {
      return { message: 'success' }
    }
  }
  return null
}

const ensureValidAuthentication = async () => {
  if (isAuthenticatedRoute()) {
    if (isRefreshTokenExpired()) {
      emitRefreshOrAccessTokenChangedEvent()
      return false
    }

    if (isTokenExpired()) {
      return await refreshAccessToken(emitRefreshOrAccessTokenChangedEvent)
    }
  }

  return true
}

/**
 * @typedef {Object} MethodOptions
 * @property {boolean} check
 * @property {string} version
 * @property {boolean} validateToken
 * @property {Object} requestOptions
 */

/**
 * @param {string} url
 * @param {MethodOptions} options
 * @returns {object}
 */
export const getReq = async (url, options = {}) => {
  const { check = true, version = 'v1', validateToken = true, requestOptions = {}, responseType = 'json' } = options
  if (validateToken && !(await ensureValidAuthentication())) return

  if (navigator.onLine) {
    try {
      const headers = setHeaders('application/json, text/plain', check)

      const reqParams = {
        method: 'GET',
        headers,
        ...requestOptions
      }

      const url_completa = `${API_URL}${version}${url}`

      const resp = await fetch(url_completa, reqParams)

      return await parseResp(resp, responseType)
    } catch (e) {
      console.warn(`Erro ao carregar ${url}: ${e}`)
      throw e
    }
  } else {
    toast.error(
      <CustomToast
        title='Erro!'
        message='Sem conexão com a Internet, favor tentar novamente ou contactar o suporte!'
      />,
      { autoClose: true, toastId: 'toastError' }
    )
  }
  return null
}

export const getReqWithSignal = async (label, url, check, options = {}, version = 'v1') => {
  if (invalidTokens()) return
  if (!navigator.onLine) {
    toast.error(
      <CustomToast
        title='Erro!'
        message='Sem conexão com a Internet, favor tentar novamente ou contactar o suporte!'
      />,
      { autoClose: true, toastId: 'toastError' }
    )
    return null
  }

  try {
    const headers = setHeaders('application/json, text/plain', check)
    const reqParams = {
      method: 'GET',
      headers,
      ...options
    }

    const url_completa = `${API_URL}${version}${url}`

    const resp = await fetch(url_completa, reqParams)
    if (resp.status === 401) {
      handleAlertLogoff()
      return
    }
    if (resp.status === 406) {
      if (!alertedMultipleLogins) {
        alertedMultipleLogins = true
        handleAlertMoreThanOneLogOn()
      }
      return
    }

    return await getResp(label, resp)
  } catch (e) {
    if (e.name === 'AbortError') return null
    console.warn(`Erro ao carregar ${label}: ${e}`)
  }

  return null
}

/**
 * @param {string} url
 * @param {Object} body
 * @param {MethodOptions} options
 * @returns {object}
 */
export const postReq = async (url, body, options = {}) => {
  const {
    check = true,
    version = 'v1',
    validateToken = true,
    requestOptions = {},
    responseType = 'json'
  } = options
  if (validateToken && !(await ensureValidAuthentication())) return

  if (navigator.onLine) {
    try {
      const headers = setHeaders('application/json, text/plain', check)

      const reqParams = {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        ...requestOptions
      }

      const url_completa = `${API_URL}${version}${url}`

      const resp = await fetch(url_completa, reqParams)
      return await parseResp(resp, responseType)
    } catch (e) {
      console.warn(`Erro ao criar ${url}: ${e}`)
      if (
        e.message?.includes('413')
        ||
        e.message?.toLowerCase().includes('too large')
        ||
        (docRequests.some(docReq => url.includes(docReq)) && e.message?.includes("Failed to fetch"))) {
        return toast.error(
          <CustomToast
            title='Erro!'
            message='A solicitação foi grande demais. Por favor, reenvie em partes menores ou com anexos reduzidos.'
          />
        )
      } else throw e
    }
  } else {
    toast.error(
      <CustomToast
        title='Erro!'
        message='Sem conexão com a Internet, favor tentar novamente ou contactar o suporte!'
      />,
      { autoClose: true, toastId: 'toastError' }
    )
  }
  return null
}

/**
 * @param {string} url
 * @param {Object} body
 * @param {MethodOptions} options
 * @returns {object}
 */
export const putReq = async (url, body, options = {}) => {
  const { check = true, version = 'v1', validateToken = true, requestOptions = {}, responseType = 'json' } = options
  if (validateToken && !(await ensureValidAuthentication())) return

  if (navigator.onLine) {
    try {
      const headers = setHeaders('application/json, text/plain', check)

      const reqParams = {
        method: 'PUT',
        headers,
        body: JSON.stringify(body),
        ...requestOptions
      }

      const url_completa = `${API_URL}${version}${url}`

      const resp = await fetch(url_completa, reqParams)
      return await parseResp(resp, responseType)
    } catch (e) {
      console.warn(`Erro ao atualizar ${url}: ${e}`)
      throw e
    }
  } else {
    toast.error(
      <CustomToast
        title='Erro!'
        message='Sem conexão com a Internet, favor tentar novamente ou contactar o suporte!'
      />,
      { autoClose: true, toastId: 'toastError' }
    )
  }
}

/**
 * @param {string} url
 * @param {MethodOptions} options
 * @returns {object}
 */
export const deleteReq = async (url, options = {}) => {
  const { check = true, version = 'v1', validateToken = true, requestOptions = {}, responseType = 'json' } = options
  if (validateToken && !(await ensureValidAuthentication())) return

  if (navigator.onLine) {
    try {
      const headers = setHeaders('application/json, text/plain', check)

      const reqParams = {
        method: 'DELETE',
        headers,
        ...requestOptions
      }

      const url_completa = `${API_URL}${version}${url}`

      const resp = await fetch(url_completa, reqParams)
      return await parseResp(resp, responseType)
    } catch (e) {
      console.warn(`Erro ao remover ${url}: ${e}`)
      throw e
    }
  } else {
    toast.error(
      <CustomToast
        title='Erro!'
        message='Sem conexão com a Internet, favor tentar novamente ou contactar o suporte!'
      />,
      { autoClose: true, toastId: 'toastError' }
    )
  }
  return null
}

/**
 * @param {string} url
 * @param {Object} body
 * @param {MethodOptions} options
 * @returns {object}
 */
export const sendFile = async (url, body, options = {}) => {
  const { check = true, version = 'v1', validateToken = true, requestOptions = {}, responseType = 'json' } = options
  if (validateToken && !(await ensureValidAuthentication())) return

  if (navigator.onLine) {
    try {
      const headers = setHeaders('', check)

      const reqParams = {
        method: "POST",
        headers,
        body,
        ...requestOptions
      }

      const url_completa = `${API_URL}${version}${url}`
      const resp = await fetch(url_completa, reqParams)
      return await parseResp(resp, responseType)
    } catch (e) {
      console.warn(`Erro ao enviar imagem do ${url}: ${e} `)
      throw e
    }
  }
}

function rememberTSAndReload(serverTS) {
  localStorage.setItem('lastTS', serverTS)
  location.reload(true)
}

async function handleUpdates() {
  const resp = await fetch('/timestamp.txt')
  if (!resp.ok) {
    console.error('Erro ao buscar o timestamp.')
    rememberTSAndReload()
    return
  }
  const serverTS = await resp.text()
  const clientTS = localStorage.getItem('lastTS')

  if (clientTS === null) {
    localStorage.setItem('lastTS', serverTS)
    setTimeout(handleUpdates, nextCheck)
    return
  }

  if (clientTS !== serverTS) {
    if (isAuthenticatedRoute() && isUserLoggedIn()) {
      MySwal.fire({
        title: 'Novas atualizações disponíveis',
        text: 'Deseja recarregar a página agora?',
        iconHtml: <AlertCircle size={80} />,
        icon: 'warning',
        padding: '4px 0 43px 0',
        width: '512px',
        showCancelButton: true,
        confirmButtonText: 'Sim, recarregar!',
        cancelButtonText: 'Mais tarde',
        customClass: {
          confirmButton: 'btn btn-primary ms-1',
          cancelButton: 'btn btn-outline-primary'
        },
        buttonsStyling: false
      }).then((result) => {
        if (result.isConfirmed) {
          rememberTSAndReload(serverTS)
        } else {
          setTimeout(handleUpdates, 60000)
        }
      })
    } else {
      // Se a rota for pública, só atualiza o timestamp e recarrega sem modal
      rememberTSAndReload(serverTS)
    }
  } else {
    setTimeout(handleUpdates, nextCheck)
    // 43200000 = 12 hours in ms, this should be configurable per env
    nextCheck = Math.min(2 * nextCheck, 43200000)
  }
}

if (process.env.REACT_APP_UPDATE_WARN === 'true') {
  handleUpdates()
}
